import React, { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { AnimatePresence, motion, MotionConfig } from 'framer-motion'
import { getOptimalAnimationSettings } from '../utils/browserOptimization'

interface CachedPage {
  component: React.ReactNode
  timestamp: number
  isActive: boolean
}

interface PageCacheProps {
  children: React.ReactNode
  maxCacheSize?: number
}

// Apple-style page persistence with smooth transitions
export const PageCache: React.FC<PageCacheProps> = ({ 
  children, 
  maxCacheSize = 10 // Limit memory usage
}) => {
  const location = useLocation()
  const [cachedPages, setCachedPages] = useState<Record<string, CachedPage>>({})
  const [activeRoute, setActiveRoute] = useState(location.pathname)
  const previousRoute = useRef<string>('')
  
  // Get optimal animation settings for current browser
  const animSettings = getOptimalAnimationSettings()

  // Update active route and cache the current page
  useEffect(() => {
    const currentRoute = location.pathname
    
    // Store the previous route
    if (activeRoute !== currentRoute) {
      previousRoute.current = activeRoute
    }
    
    setActiveRoute(currentRoute)
    
    // Cache the current page
    setCachedPages(prev => {
      const newCache = { ...prev }
      
      // Add or update current page
      newCache[currentRoute] = {
        component: children,
        timestamp: Date.now(),
        isActive: true
      }
      
      // Mark all other pages as inactive
      Object.keys(newCache).forEach(route => {
        if (route !== currentRoute) {
          newCache[route].isActive = false
        }
      })
      
      // Implement LRU cache - remove oldest pages if cache is too large
      const cacheEntries = Object.entries(newCache)
      if (cacheEntries.length > maxCacheSize) {
        // Sort by timestamp and remove oldest
        const sortedEntries = cacheEntries.sort((a, b) => b[1].timestamp - a[1].timestamp)
        const keepEntries = sortedEntries.slice(0, maxCacheSize)
        
        const trimmedCache: Record<string, CachedPage> = {}
        keepEntries.forEach(([route, page]) => {
          trimmedCache[route] = page
        })
        
        return trimmedCache
      }
      
      return newCache
    })
  }, [location.pathname, children, activeRoute, maxCacheSize])

  // Preload critical pages for instant navigation
  useEffect(() => {
    const criticalRoutes = ['/', '/about', '/contact', '/booking']
    
    // Preload critical routes after initial page load
    const preloadTimer = setTimeout(() => {
      criticalRoutes.forEach(route => {
        if (!cachedPages[route] && route !== activeRoute) {
          // Create invisible link elements to trigger route preloading
          const link = document.createElement('link')
          link.rel = 'prefetch'
          link.href = route
          document.head.appendChild(link)
        }
      })
    }, 2000) // Wait 2 seconds after initial load
    
    return () => clearTimeout(preloadTimer)
  }, [cachedPages, activeRoute])

  return (
    <MotionConfig reducedMotion={animSettings.reducedMotion ? 'always' : 'never'}>
      <div 
        className="page-cache-container"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#F7F5F5',
          zIndex: 1,
          // Cross-browser optimization
          WebkitTransform: 'translate3d(0, 0, 0)',
          transform: 'translate3d(0, 0, 0)',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
          WebkitPerspective: 1000,
          perspective: 1000
        }}
      >
        <AnimatePresence mode="wait" onExitComplete={() => {
        // Clean up after transitions
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
        window.scrollTo(0, 0)
      }}>
        {Object.entries(cachedPages).map(([route, page]) => (
          <motion.div
            key={route}
            initial={route === activeRoute ? { 
              opacity: 0, 
              y: 30,
              scale: 0.96,
              zIndex: 20
            } : false}
            animate={route === activeRoute ? { 
              opacity: 1, 
              y: 0,
              scale: 1,
              zIndex: 20
            } : {
              opacity: 0,
              y: route === previousRoute.current ? -20 : 0,
              scale: route === previousRoute.current ? 0.95 : 1,
              zIndex: route === previousRoute.current ? 10 : 5
            }}
            exit={{ 
              opacity: 0.3,
              y: -20,
              scale: 0.95,
              zIndex: 10
            }}
            transition={{
              duration: animSettings.duration,
              ease: animSettings.ease,
              opacity: { duration: animSettings.duration * 0.75, ease: "easeInOut" },
              y: { duration: animSettings.duration, ease: "easeInOut" },
              scale: { duration: animSettings.duration * 0.875, ease: "easeInOut" }
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: '#F7F5F5',
              overflow: route === activeRoute ? 'auto' : 'hidden',
              pointerEvents: route === activeRoute ? 'auto' : 'none',
              willChange: route === activeRoute ? 'transform, opacity' : 'auto',
              WebkitBackfaceVisibility: 'hidden',
              backfaceVisibility: 'hidden',
              WebkitTransform: 'translate3d(0, 0, 0)',
              transform: 'translate3d(0, 0, 0)',
              // Reduce contain to prevent Chrome flickering
              contain: 'layout style',
              isolation: 'isolate'
            }}
          >
            {/* Subtle shadow for depth */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: route === activeRoute ? 1 : 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                boxShadow: route === activeRoute 
                  ? '0 -8px 32px rgba(0, 0, 0, 0.12)' 
                  : '0 -4px 16px rgba(0, 0, 0, 0.08)',
                pointerEvents: 'none',
                zIndex: 1
              }}
            />

            {/* Premium glow effect for active page */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: route === activeRoute ? 1 : 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.04) 0%, transparent 60%)',
                pointerEvents: 'none',
                zIndex: 2
              }}
            />
            
            {/* Page content */}
            <motion.div 
              style={{ 
                position: 'relative', 
                zIndex: 3, 
                minHeight: '100vh',
                backgroundColor: '#F7F5F5',
                // Cross-browser optimization
                WebkitTransform: 'translate3d(0, 0, 0)',
                transform: 'translate3d(0, 0, 0)',
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden'
              }}
              initial={route === activeRoute ? { opacity: 0, y: 15 } : false}
              animate={route === activeRoute ? { opacity: 1, y: 0 } : { opacity: 0 }}
              transition={{ 
                duration: 0.25, 
                delay: route === activeRoute ? 0.15 : 0,
                ease: "easeOut"
              }}
            >
              {page.component}
            </motion.div>
          </motion.div>
        ))}
        </AnimatePresence>
      </div>
    </MotionConfig>
  )
}

// Hook for managing page cache state
export const usePageCache = () => {
  const location = useLocation()
  const [cacheStats, setCacheStats] = useState({
    totalPages: 0,
    currentRoute: location.pathname,
    cacheHits: 0
  })

  useEffect(() => {
    setCacheStats(prev => ({
      ...prev,
      currentRoute: location.pathname,
      cacheHits: prev.currentRoute !== location.pathname ? prev.cacheHits + 1 : prev.cacheHits
    }))
  }, [location.pathname])

  return cacheStats
}

// Performance monitoring for page cache (now handled by DeveloperConsole)
export const PageCacheMonitor: React.FC = () => {
  // This component is deprecated - use DeveloperConsole instead
  return null
}

// Memory cleanup utility
export const useMemoryCleanup = () => {
  useEffect(() => {
    const cleanup = () => {
      // Force garbage collection if available (Chrome DevTools)
      if ('gc' in window && typeof (window as any).gc === 'function') {
        (window as any).gc()
      }
    }
    
    // Cleanup on page visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setTimeout(cleanup, 5000) // Cleanup after 5 seconds of being hidden
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])
}